import React, { useState, useEffect, useRef } from 'react';
import providerService from 'services/providerService';
import authService from 'services/authService';
import Swal from 'sweetalert2';
import 'styles/manager.css';

export const ProvidersView = ({ onUpdatePaymentDays, onEditProvider, onAddProvider }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [paymentDays, setPaymentDays] = useState('');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  // Solo la primera carga muestra el spinner de pantalla completa
  const [initialLoad, setInitialLoad] = useState(true);
  // Evita que una respuesta vieja sobrescriba una más reciente
  const requestIdRef = useRef(0);

  // --- Estado de paginación ---
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    totalCount: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  // Carga inicial + recarga al cambiar la búsqueda (página 1).
  // Sin texto carga inmediato; con texto espera 400ms (debounce).
  useEffect(() => {
    const delay = setTimeout(() => {
      loadProviders(1);
    }, searchTerm ? 400 : 0);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  const loadProviders = async (page = currentPage) => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      // Pasa page, limit y el término de búsqueda al servicio
      const response = await providerService.getProviders({
        page,
        limit: pagination.limit,
        search: searchTerm,
      });

      if (requestId !== requestIdRef.current) return; // respuesta vieja
      if (!response.error) {
        setProviders(response.data.data || []);
        setPagination(response.data.pagination);
        setCurrentPage(response.data.pagination.page);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los proveedores',
        timer: 3000,
        showConfirmButton: false,
      });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    loadProviders(newPage);
  };

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleUpdatePaymentConfig = async () => {
    if (!selectedProvider || selectedProvider === 'Seleccione un proveedor') {
      Swal.fire({ icon: 'warning', title: 'Atención', text: 'Seleccione un proveedor', timer: 2000, showConfirmButton: false });
      return;
    }
    if (!paymentDays || paymentDays < 1) {
      Swal.fire({ icon: 'warning', title: 'Atención', text: 'Ingrese un número válido de días', timer: 2000, showConfirmButton: false });
      return;
    }

    try {
      const response = await providerService.updateProvider(selectedProvider, { dias_pago: parseInt(paymentDays) });
      if (!response.error) {
        Swal.fire({ icon: 'success', title: '¡Actualizado!', text: 'Configuración de pagos actualizada correctamente', timer: 2000, showConfirmButton: false });
        setSelectedProvider('');
        setPaymentDays('');
        loadProviders(currentPage);
        if (onUpdatePaymentDays) onUpdatePaymentDays(selectedProvider, parseInt(paymentDays));
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo actualizar la configuración', timer: 3000 });
    }
  };

  const handleEdit = async (provider) => {
    const result = await Swal.fire({
      title: 'Editar Proveedor',
      html: `
        <div style="text-align: left;">
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>Nombre:</strong></label>
            <input id="edit-nombre" class="swal2-input" value="${provider.nombre}" style="margin: 0; width: 90%;">
          </div>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>RFC:</strong></label>
            <input id="edit-rfc" class="swal2-input" value="${provider.rfc}" style="margin: 0; width: 90%;">
          </div>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>Email:</strong></label>
            <input id="edit-email" type="email" class="swal2-input" value="${provider.email}" style="margin: 0; width: 90%;">
          </div>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>Teléfono:</strong></label>
            <input id="edit-telefono" class="swal2-input" value="${provider.telefono || ''}" style="margin: 0; width: 90%;">
          </div>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>Días de Pago:</strong></label>
            <input id="edit-dias-pago" type="number" class="swal2-input" value="${provider.dias_pago}" style="margin: 0; width: 90%;">
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => ({
        nombre: document.getElementById('edit-nombre').value,
        rfc: document.getElementById('edit-rfc').value,
        email: document.getElementById('edit-email').value,
        telefono: document.getElementById('edit-telefono').value,
        dias_pago: parseInt(document.getElementById('edit-dias-pago').value),
      }),
    });

    if (result.isConfirmed) {
      try {
        const response = await providerService.updateProvider(provider.id, result.value);
        if (!response.error) {
          Swal.fire({ icon: 'success', title: '¡Actualizado!', text: 'Proveedor actualizado correctamente', timer: 2000, showConfirmButton: false });
          loadProviders(currentPage);
          if (onEditProvider) onEditProvider(provider.id);
        }
      } catch {
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo actualizar el proveedor', timer: 3000 });
      }
    }
  };

  const handleAdd = async () => {
    const result = await Swal.fire({
      title: 'Agregar Proveedor',
      html: `
        <div style="text-align: left;">
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>Nombre: *</strong></label>
            <input id="add-nombre" class="swal2-input" placeholder="Nombre del proveedor" style="margin: 0; width: 90%;">
          </div>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>RFC: *</strong></label>
            <input id="add-rfc" class="swal2-input" placeholder="ABC123456XYZ" style="margin: 0; width: 90%;">
          </div>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>Razón Social: *</strong></label>
            <input id="add-razon-social" class="swal2-input" placeholder="Empresa SA de CV" style="margin: 0; width: 90%;">
          </div>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>Email: *</strong></label>
            <input id="add-email" type="email" class="swal2-input" placeholder="correo@ejemplo.com" style="margin: 0; width: 90%;">
          </div>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>Teléfono:</strong></label>
            <input id="add-telefono" class="swal2-input" placeholder="8112345678" style="margin: 0; width: 90%;">
          </div>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>Dirección:</strong></label>
            <textarea id="add-direccion" class="swal2-input" placeholder="Dirección completa" style="margin: 0; width: 90%; height: 80px;"></textarea>
          </div>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px;"><strong>Días de Pago: *</strong></label>
            <input id="add-dias-pago" type="number" class="swal2-input" value="15" style="margin: 0; width: 90%;">
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Crear',
      cancelButtonText: 'Cancelar',
      width: 600,
      preConfirm: () => {
        const nombre = document.getElementById('add-nombre').value;
        const rfc = document.getElementById('add-rfc').value;
        const razonSocial = document.getElementById('add-razon-social').value;
        const email = document.getElementById('add-email').value;
        const diasPago = document.getElementById('add-dias-pago').value;
        if (!nombre || !rfc || !razonSocial || !email || !diasPago) {
          Swal.showValidationMessage('Completa todos los campos obligatorios (*)');
          return false;
        }
        return {
          nombre,
          rfc: rfc.toUpperCase(),
          razon_social: razonSocial,
          email,
          telefono: document.getElementById('add-telefono').value,
          direccion: document.getElementById('add-direccion').value,
          dias_pago: parseInt(diasPago),
        };
      },
    });

    if (result.isConfirmed) {
      try {
        const response = await providerService.createProvider(result.value);
        if (!response.error) {
          Swal.fire({ icon: 'success', title: '¡Creado!', text: 'Proveedor creado correctamente', timer: 2000, showConfirmButton: false });
          loadProviders(currentPage);
          if (onAddProvider) onAddProvider();
        }
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: error.message || 'No se pudo crear el proveedor', timer: 3000 });
      }
    }
  };

  const handleToggleStatus = async (provider) => {
    const newStatus = !provider.activo;
    const action = newStatus ? 'activar' : 'desactivar';

    const result = await Swal.fire({
      title: `¿${action.charAt(0).toUpperCase() + action.slice(1)} proveedor?`,
      text: `Se ${action}á el proveedor ${provider.nombre}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Sí, ${action}`,
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        const response = await providerService.updateProvider(provider.id, { activo: newStatus });
        if (!response.error) {
          Swal.fire({ icon: 'success', title: '¡Actualizado!', text: `Proveedor ${action}do correctamente`, timer: 2000, showConfirmButton: false });
          loadProviders(currentPage);
        }
      } catch {
        Swal.fire({ icon: 'error', title: 'Error', text: `No se pudo ${action} el proveedor`, timer: 3000 });
      }
    }
  };

  // ─── Render páginas ─────────────────────────────────────────────────────────
  const renderPageNumbers = () => {
    const pages = [];
    const { totalPages } = pagination;

    // Muestra máximo 5 páginas centradas en la actual
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${i === currentPage ? 'pagination-btn--active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  // ─── Loading ─────────────────────────────────────────────────────────────────
  if (initialLoad) {
    return (
      <div className="providers-view">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando proveedores...</p>
        </div>
      </div>
    );
  }


  const handleResetPassword = async (provider) => {
  const result = await Swal.fire({
    title: '¿Resetear contraseña?',
    text: `Se generará una nueva contraseña para ${provider.nombre}`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Sí, resetear',
    cancelButtonText: 'Cancelar',
  });

  if (!result.isConfirmed) return;

  try {
    const response = await authService.resetUserPassword(provider.usuario_id || provider.user_id);

    if (!response.error) {
      Swal.fire({
        icon: 'success',
        title: 'Contraseña reseteada',
        text: 'Se generó una nueva contraseña correctamente',
        timer: 2000,
        showConfirmButton: false,
      });
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.message || 'No se pudo resetear la contraseña',
      timer: 3000,
    });
  }
};

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="providers-view">
      <div className="card">
        <div className="card-header">Gestión de Proveedores</div>

        <div className="providers-actions">
          <input
            type="text"
            className="search-box"
            placeholder="Buscar proveedores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="btn btn-primary" onClick={handleAdd}>
            + Agregar Proveedor
          </button>
        </div>

        <div className="providers-list" style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
          {providers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <div className="empty-text">No se encontraron proveedores</div>
            </div>
          ) : (
            providers.map((provider) => (
              <div key={provider.id} className="provider-card">
                <div className="provider-info">
                  <h3 className="provider-name">{provider.nombre}</h3>
                  <div className="provider-details-grid">
                    <div className="provider-detail">
                      <div className="detail-label">RFC</div>
                      <div className="detail-value rfc-text">{provider.rfc}</div>
                    </div>
                    <div className="provider-detail">
                      <div className="detail-label">Email</div>
                      <div className="detail-value">{provider.email}</div>
                    </div>
                    <div className="provider-detail">
                      <div className="detail-label">Días de Pago</div>
                      <div className="detail-value">{provider.dias_pago} días</div>
                    </div>
                    <div className="provider-detail">
                      <div className="detail-label">Estado</div>
                      <div className="detail-value">
                        <span
                          className={`status-badge ${provider.activo ? 'status-aprobada' : 'status-rechazada'}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleToggleStatus(provider)}
                        >
                          {provider.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="provider-actions">
                  <button className="btn btn-edit" onClick={() => handleEdit(provider)}>
                    <i className="bi bi-pencil"></i>
                  </button>
                   <button
                          className="btn btn-warning"
                          onClick={() => handleResetPassword(provider)}
                          title="Resetear contraseña"
                        >
                          <i className="bi bi-key"></i>
                        </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── Paginación ── */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <div className="pagination-info">
              Mostrando {providers.length} de {pagination.totalCount} proveedores
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={!pagination.hasPrev}
              >
                «
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPrev}
              >
                ‹
              </button>

              {renderPageNumbers()}

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNext}
              >
                ›
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.hasNext}
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Configuración de Fechas de Pago ── */}
      <div className="card shadow-sm">

        <div className="card-header d-flex align-items-center gap-2">
          <i className="bi bi-gear-fill text-secondary"></i>
          <span className="fw-semibold">Configuración de Fechas de Pago</span>
        </div>

        <div className="card-body">

          <div className="row g-3 align-items-end">

            {/* Proveedor */}
            <div className="col-12 col-md-5">
              <label className="form-label fw-semibold">Proveedor</label>
              <select
                className="form-select"
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
              >
                <option value="">Seleccione un proveedor</option>
                {providers
                  .filter((p) => p.activo)
                  .map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.nombre} ({provider.dias_pago} días actuales)
                    </option>
                  ))}
              </select>
            </div>

            {/* Días de pago */}
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">
                Días de Pago desde Recepción
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="15"
                value={paymentDays}
                onChange={(e) => setPaymentDays(e.target.value)}
                min="1"
              />
            </div>

            {/* Botón */}
            <div className="col-12 col-md-auto">
              <button
                className="btn btn-primary w-100 px-4"
                onClick={handleUpdatePaymentConfig}
                disabled={!selectedProvider || !paymentDays}
              >
                <i className="bi bi-save me-2"></i>
                Guardar Configuración
              </button>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
};